import { useEffect, useState } from 'react';

/**
 * Reactive media-query match, shared by the shell chrome.
 *
 * Lives here (not in `shared/hooks`) because both consumers are shell-only:
 * `Shell` uses it to decide whether the nav rails are in off-canvas mode,
 * `Topbar` uses it to swap the personal links for their overflow menu.
 *
 * Without `matchMedia` (SSR, older jsdom) it reports `false`. That is the
 * safe direction FOR SHELL ONLY — the desktop layout renders and nothing is
 * hidden. It is NOT safe for `Topbar`: its `false` branch renders
 * `.topbar__personal`, which App.css hides unconditionally below 860px via
 * plain CSS that does not consult `matchMedia`. A narrow viewport with no
 * `matchMedia` would therefore get NEITHER affordance — precisely the
 * unreachable-surfaces bug the overflow menu exists to fix. Callers whose
 * `false` branch can be hidden by CSS must consult
 * {@link matchMediaSupported} and prefer the always-visible affordance.
 */
/* ── MOBILE ── shell breakpoint. At/below this width the Dock + ModuleRail
   collapse into a left off-canvas drawer (see `.shell-nav` in App.css), and
   Freya's floating launcher is not rendered at all. Kept in sync with the
   `@media (max-width: 768px)` blocks there. It lives here rather than in
   Shell.tsx because FreyaWidget reads it too, and Shell imports FreyaWidget —
   importing it back from Shell would be a cycle. */
export const SHELL_MOBILE_QUERY = '(max-width: 768px)';

export function matchMediaSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

export function useMediaMatch(query: string): boolean {
  const [match, setMatch] = useState<boolean>(() =>
    matchMediaSupported() ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    if (!matchMediaSupported()) return;
    const mq = window.matchMedia(query);
    const onChange = () => setMatch(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [query]);
  return match;
}
